import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { SerializedConstructor } from '@langchain/core/load/serializable';
import { Option, Select } from '@mui/joy';

import { useId, useState } from 'react';

import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/Command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/Popover';
import { EPromptType } from '@/types/schema';
import { cn } from '@/utils/tailwind';

import {
  CHAT_MODEL_MAPPING,
  MODEL_DEFAULT_KWARGS,
  MODEL_SECRETS,
  STRUCTURED_MODELS,
  getModelDisplayName,
} from '../../Playground.constants';
import { applySecret } from '../../utils/Playground.utils';
import {
  ManifestVariant,
  tableVariantLabelClass,
  tableVariantRowClass,
} from './Manifest.utils';

const CHAT_MODELS_TO_SHOW = Object.values(CHAT_MODEL_MAPPING).filter(
  (model) => model.modelId !== 'langchain/chat_models/bedrock/ChatBedrock'
);

function ManifestSelectProvider(props: {
  show: 'all' | 'structured';
  value: string;
  onChange: (value: typeof props.value) => void;
  readOnly?: boolean;
  variant?: ManifestVariant;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const id = useId();

  const modelLabel = getModelDisplayName(props.value);

  return (
    <div
      className={cn(
        'flex flex-col items-stretch gap-2',
        props.variant === 'table' && tableVariantRowClass
      )}
    >
      <span
        className={cn(
          'my-auto text-sm font-medium',
          props.variant === 'table' && tableVariantLabelClass
        )}
      >
        Provider
      </span>

      {props.readOnly ? (
        <div className={'overflow-hidden text-ellipsis'}>
          {props.value == null ? 'No provider selected...' : modelLabel}
        </div>
      ) : (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              id={id}
              className="flex items-center gap-2 rounded-md border border-secondary p-1.5 px-2.5 transition-colors hover:bg-secondary-hover active:bg-secondary"
              data-testid="provider-select-button"
            >
              <span className="flex min-w-0 flex-shrink grow items-center gap-2 overflow-hidden text-ellipsis text-left">
                {props.value == null ? 'Select model...' : modelLabel}
              </span>
              <ChevronDownIcon
                className={cn(
                  'mr-0.5 h-5 w-5 transition-all',
                  open && 'rotate-180'
                )}
              />
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            className="max-h-[50vh] min-w-[375px] overflow-auto p-0"
          >
            <Command>
              <label className="p-4 pb-0 text-sm font-medium leading-tight">
                Select a provider...
              </label>

              <CommandInput
                className="m-2 my-3 mb-2 px-2 py-3"
                value={search}
                onValueChange={setSearch}
              />

              <CommandList>
                {props.show === 'structured' && (
                  <CommandGroup className="m-0 [&_[cmdk-group-heading]]:bg-secondary [&_[cmdk-group-heading]]:px-3.5 [&_[cmdk-group-items]]:py-1.5">
                    {STRUCTURED_MODELS.map(({ modelId, displayName }) => (
                      <CommandItem
                        key={modelId}
                        onSelect={() => {
                          props.onChange(modelId);
                          setOpen(false);
                        }}
                        className="mx-1.5 flex cursor-pointer items-center px-2"
                      >
                        {displayName}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}

                {props.show === 'all' && (
                  <CommandGroup className="m-0 [&_[cmdk-group-heading]]:bg-secondary [&_[cmdk-group-heading]]:px-3.5 [&_[cmdk-group-items]]:py-1.5">
                    {CHAT_MODELS_TO_SHOW.map(({ modelId, displayName }) => (
                      <CommandItem
                        key={modelId}
                        onSelect={() => {
                          props.onChange(modelId);
                          setOpen(false);
                        }}
                        className="mx-1.5 flex cursor-pointer items-center px-2"
                      >
                        {displayName}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}

export function ManifestProviderReplace(props: {
  variant: ManifestVariant;
  promptType: EPromptType;
  value: SerializedConstructor;
  onChange: (value: SerializedConstructor) => void;
  readOnly?: boolean;
}) {
  return (
    <ManifestSelectProvider
      show={props.promptType === EPromptType.STRUCTURED ? 'structured' : 'all'}
      value={props.value.id.join('/')}
      readOnly={props.readOnly}
      onChange={(modelId) => {
        props.onChange(
          applySecret(
            {
              lc: 1,
              type: 'constructor',
              id: modelId?.split('/'),
              kwargs: MODEL_DEFAULT_KWARGS[modelId],
            },
            MODEL_SECRETS[modelId]
          )
        );
      }}
      variant={props.variant}
    />
  );
}

export function ManifestProviderReplaceWithSwitch(props: {
  variant: ManifestVariant;
  value: SerializedConstructor;
  onChange: (value: SerializedConstructor) => void;
  readOnly?: boolean;
  isChatModel: boolean;
  otherModelId: string;
}) {
  return (
    <div className="flex items-end justify-center gap-2">
      <div className="flex-grow">
        <ManifestProviderReplace
          variant={props.variant}
          promptType={
            props.variant === 'evaluator'
              ? EPromptType.STRUCTURED
              : EPromptType.CHAT
          }
          onChange={props.onChange}
          value={props.value}
          readOnly={props.readOnly}
        />
      </div>

      {!props.readOnly && props.variant !== 'evaluator' && (
        <Select
          value={props.isChatModel ? EPromptType.CHAT : EPromptType.INSTRUCT}
          onChange={(_, value) => {
            if (
              (props.isChatModel && value === EPromptType.INSTRUCT) ||
              (!props.isChatModel && value === EPromptType.CHAT)
            ) {
              props.onChange(
                applySecret(
                  {
                    lc: 1,
                    type: 'constructor',
                    id: props.otherModelId?.split('/'),
                    kwargs: {},
                  },
                  MODEL_SECRETS[props.otherModelId]
                )
              );
            }
          }}
          className="h-[32px]"
        >
          <Option value={EPromptType.CHAT}>Chat</Option>
          <Option value={EPromptType.INSTRUCT}>Instruct</Option>
        </Select>
      )}
    </div>
  );
}
